// TermsAndConditions.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Container,
    Paper,
    Typography,
    Box,
    Divider,
    Button,
    FormControlLabel,
    Checkbox,
    Stack,
} from "@mui/material";

/**
 * TermsAndConditions component
 *
 * Props:
 *  - lastUpdated: string (displayed date)
 *  - showAccept: boolean (if true shows checkbox + Accept button)
 *  - onAccept: function called when user accepts (only if showAccept)
 */
export default function OwnersTerms({
    lastUpdated = new Date().toLocaleDateString(),
    showAccept = false,
    onAccept = null,
}) {
    const [accepted, setAccepted] = useState(false);

    const handlePrint = () => {
        window.print();
    };

    const handleAccept = () => {
        if (onAccept) onAccept();
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
            <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h6" component="h1" gutterBottom>
                            TERMS AND CONDITIONS — SALON OWNERS (VENDORS)
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            GroomWell Digital Services – Vendor Terms of Use
                        </Typography>
                    </Box>

                    <Box textAlign="right">
                        <Typography variant="caption" display="block" color="text.secondary">
                            Last Updated:
                        </Typography>
                        <Typography variant="body2">{lastUpdated}</Typography>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {/* Scrollable content area for mobile */}
                <Box
                    sx={{
                        maxHeight: { xs: "55vh", md: "60vh" },
                        overflowY: "auto",
                        pr: 1,
                    }}
                >
                    <Section title="1. Acceptance of Terms">
                        By signing up as a Salon Owner on GroomWell Digital Services ("Platform", "We", "Us"), you agree to these
                        Terms and Conditions. If you do not agree, you must not register or use the platform.
                    </Section>

                    <Section title="2. Eligibility">
                        <Typography paragraph>
                            You represent and warrant that:
                        </Typography>
                        <ul>
                            <li>You are the legal owner or authorized representative of the salon you register.</li>
                            <li>Your salon is compliant with all applicable laws, licenses, and regulations in India.</li>
                            <li>All information you provide is accurate and truthful.</li>
                        </ul>
                    </Section>

                    <Section title="3. Platform Services">
                        <Typography paragraph>
                            GroomWell provides:
                        </Typography>
                        <ul>
                            <li>A listing service for salons</li>
                            <li>Appointment booking interface</li>
                            <li>User review and rating system</li>
                            <li>Communication and notification tools</li>
                        </ul>

                        <Typography paragraph>
                            GroomWell does NOT:
                        </Typography>
                        <ul>
                            <li>Provide salon services</li>
                            <li>Guarantee customer bookings</li>
                            <li>Handle or process payments between client and salon</li>
                            <li>Mediate disputes between clients and salon</li>
                        </ul>

                        <Typography paragraph>All services are provided on an "as is" and "as available" basis.</Typography>
                    </Section>

                    <Section title="4. Monthly Subscription Fee">
                        <ul>
                            <li>Salon Owners pay a monthly subscription fee to use the Platform.</li>
                            <li>Fees are non refundable unless required by applicable law.</li>
                            <li>GroomWell may revise the fee with prior notice.</li>
                            <li>Failure to pay may result in suspension or termination of your account.</li>
                        </ul>
                    </Section>

                    <Section title="5. Salon Information and Content">
                        <Typography paragraph>You are responsible for all content you upload, including:</Typography>
                        <ul>
                            <li>Salon name and details</li>
                            <li>Service descriptions</li>
                            <li>Images and videos</li>
                            <li>Prices</li>
                            <li>Offers and promotions</li>
                        </ul>

                        <Typography paragraph>You confirm that:</Typography>
                        <ul>
                            <li>You own the rights to the content</li>
                            <li>The content does not violate any third party rights or laws</li>
                            <li>All prices and descriptions are accurate and not misleading</li>
                        </ul>

                        <Typography paragraph>GroomWell may remove or edit content that violates these terms.</Typography>
                    </Section>

                    <Section title="6. Appointment Management">
                        <ul>
                            <li>Honor confirmed appointments</li>
                            <li>Update availability and scheduling accurately</li>
                            <li>Avoid unnecessary cancellations</li>
                            <li>Provide services as described</li>
                        </ul>
                        <Typography paragraph>
                            Repeated cancellation or customer complaints may lead to account action.
                        </Typography>
                    </Section>

                    <Section title="7. Payments">
                        <Typography paragraph>
                            All payment for salon services is directly between salon and customer. GroomWell is not involved in
                            payments, disputes, refunds, or financial transactions between the parties. The salon is solely responsible
                            for GST or other taxes, billing customers, and any refund or compensation obligations.
                        </Typography>
                    </Section>

                    <Section title="8. Customer Interactions">
                        <Typography paragraph>
                            You must treat all customers fairly and professionally. Harassment, discrimination, or abusive behavior may
                            result in termination.
                        </Typography>
                    </Section>

                    <Section title="9. Reviews and Ratings">
                        <Typography paragraph>
                            Customers may leave reviews and ratings based on their experience. GroomWell does not modify or remove reviews
                            unless they violate policy. You may not manipulate or incentivize reviews.
                        </Typography>
                    </Section>

                    <Section title="10. Liability">
                        <Typography paragraph>
                            GroomWell is not liable for any damages or losses from using the platform, customer behavior, service disputes,
                            miscommunication or missed appointments, loss of profit, reputation, or data. Your use of the platform is at your
                            own risk.
                        </Typography>
                    </Section>

                    <Section title="11. Termination">
                        <Typography paragraph>
                            GroomWell may suspend or terminate your account for violation of these terms, fraudulent activities, repeated
                            customer complaints, or non payment of subscription fees. You may request account termination anytime.
                        </Typography>
                    </Section>

                    <Section title="12. Indemnification">
                        <Typography paragraph>
                            You agree to indemnify GroomWell from all claims, losses, liabilities, and expenses arising from your salon
                            services, customer disputes, content you upload, or violation of laws or third party rights.
                        </Typography>
                    </Section>

                    <Section title="13. Changes to Terms">
                        <Typography paragraph>
                            GroomWell may update these Terms anytime. Continued use of the platform means acceptance of updated Terms.
                        </Typography>
                    </Section>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems="center">
                    {/* <Box>
                        <Button variant="outlined" onClick={handlePrint} sx={{ mr: 1 }}>
                            Print / Save as PDF
                        </Button>
                    </Box> */}

                    {/* {showAccept ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <FormControlLabel
                                control={<Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />}
                                label={<Typography variant="body2">I have read and accept the Terms & Conditions</Typography>}
                            />
                            <Button variant="contained" disabled={!accepted} onClick={handleAccept}>
                                Accept
                            </Button>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                By using GroomWell you agree to these Terms.
                            </Typography>
                        </Box>
                    )} */}
                </Stack>
            </Paper>
        </Container>
    );
}

/* Small helper component for consistent section layout */
function Section({ title, children }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {title}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {children}
            </Typography>
        </Box>
    );
}

Section.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node,
};

// TermsAndConditions.propTypes = {
//     lastUpdated: PropTypes.string,
//     showAccept: PropTypes.bool,
//     onAccept: PropTypes.func,
// };
